/**
 * React Mount Bridge
 * Connects React components to the vanilla JS billing app
 */
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ConditionsForm, ConditionsData } from './components/forms/ConditionsForm';

// Track mounted roots for cleanup
let conditionsRoot: Root | null = null;

// Extend window interface for TypeScript
declare global {
  interface Window {
    mountConditionsForm: typeof mountConditionsForm;
    unmountConditionsForm: typeof unmountConditionsForm;
    updateChargeSummary?: () => void;
    updateWizardPricing?: () => void;
  }
}

/**
 * Mount the ConditionsForm React component into a container
 * @param containerId - DOM element ID to mount into
 * @param initialPaint - Initial paint condition slider value (0-9)
 * @param initialGrowth - Initial growth level slider value (0-8)
 */
export function mountConditionsForm(
  containerId: string,
  initialPaint: number = 0,
  initialGrowth: number = 0
): void {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`[React Bridge] Container #${containerId} not found`);
    return;
  }

  // Unmount existing root if present
  if (conditionsRoot) {
    conditionsRoot.unmount();
    conditionsRoot = null;
  }

  // Create new root and render
  conditionsRoot = createRoot(container);

  const handleChange = (data: ConditionsData) => {
    // Update hidden inputs for backward compatibility with existing billing logic
    const paintInput = document.getElementById('actualPaintCondition') as HTMLInputElement | null;
    const growthInput = document.getElementById('actualGrowthLevel') as HTMLInputElement | null;

    if (paintInput) {
      paintInput.value = String(data.paintCondition);
      paintInput.setAttribute('data-value', data.paintConditionValue);
      paintInput.setAttribute('data-user-set', 'true');
    }

    if (growthInput) {
      growthInput.value = String(data.growthLevel);
      growthInput.setAttribute('data-value', data.growthLevelValue);
      growthInput.setAttribute('data-user-set', 'true');
    }

    // Trigger existing pricing update functions
    if (typeof window.updateChargeSummary === 'function') {
      window.updateChargeSummary();
    }
    if (typeof window.updateWizardPricing === 'function') {
      window.updateWizardPricing();
    }

    console.log('[React Bridge] Conditions updated:', data);
  };

  conditionsRoot.render(
    <ConditionsForm
      initialPaintCondition={initialPaint}
      initialGrowthLevel={initialGrowth}
      onChange={handleChange}
    />
  );

  console.log(`[React Bridge] ConditionsForm mounted in #${containerId}`);
}

/**
 * Unmount the ConditionsForm if mounted
 */
export function unmountConditionsForm(): void {
  if (conditionsRoot) {
    conditionsRoot.unmount();
    conditionsRoot = null;
    console.log('[React Bridge] ConditionsForm unmounted');
  }
}

// Expose to window for vanilla JS access
window.mountConditionsForm = mountConditionsForm;
window.unmountConditionsForm = unmountConditionsForm;

console.log('[React Bridge] Conditions bridge loaded');
